import { lazy, Suspense, useEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { USER_LANG_KEY } from './i18n'
import { AuthProvider } from './contexts/AuthContext'
import { ProtectedRoute } from './components/layout/ProtectedRoute'
import { AppLayout } from './components/layout/AppLayout'
import { ErrorBoundary } from './components/ErrorBoundary'
import { Spinner } from './components/ui/Spinner'
import { useProfile } from './hooks/useProfile'
import { APP_URL } from './lib/constants'

const isProduction = (APP_URL.includes('tailorloop.com') || APP_URL.includes('tailorlead.app')) && !APP_URL.includes('staging')

// Lazy-loaded page components for code splitting
const LandingPage = lazy(() => import('./components/landing/LandingPage').then(m => ({ default: m.LandingPage })))
const LoginPage = lazy(() => import('./components/auth/LoginPage').then(m => ({ default: m.LoginPage })))
const SignupPage = lazy(() => import('./components/auth/SignupPage').then(m => ({ default: m.SignupPage })))
const ForgotPasswordPage = lazy(() => import('./components/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import('./components/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })))
const AuthCallback = lazy(() => import('./components/auth/AuthCallback').then(m => ({ default: m.AuthCallback })))
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })))
const CampaignsPage = lazy(() => import('./pages/CampaignsPage').then(m => ({ default: m.CampaignsPage })))
const CampaignCreatePage = lazy(() => import('./pages/CampaignCreatePage').then(m => ({ default: m.CampaignCreatePage })))
const CampaignDetailPage = lazy(() => import('./pages/CampaignDetailPage').then(m => ({ default: m.CampaignDetailPage })))
const ContactsPage = lazy(() => import('./pages/ContactsPage').then(m => ({ default: m.ContactsPage })))
const ContactDetailPage = lazy(() => import('./pages/ContactDetailPage').then(m => ({ default: m.ContactDetailPage })))
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then(m => ({ default: m.OnboardingPage })))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })))
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })))
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })))
const MockupPage = lazy(() => import('./pages/MockupPage').then(m => ({ default: m.MockupPage })))
const MetaAdsLandingPage = lazy(() => import('./pages/MetaAdsLandingPage').then(m => ({ default: m.MetaAdsLandingPage })))
const MetaAdsThanksPage = lazy(() => import('./pages/MetaAdsThanksPage').then(m => ({ default: m.MetaAdsThanksPage })))
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })))
const AccountHealthPage = lazy(() => import('./pages/AccountHealthPage').then(m => ({ default: m.AccountHealthPage })))
const LandingV2Page = lazy(() => import('./pages/LandingV2Page').then(m => ({ default: m.LandingV2Page })))
const PaidLandingPage = lazy(() => import('./pages/PaidLandingPage').then(m => ({ default: m.PaidLandingPage })))
const HelpIndexPage = lazy(() => import('./pages/help/HelpIndexPage').then(m => ({ default: m.HelpIndexPage })))
const HelpDnsSetupPage = lazy(() => import('./pages/help/HelpDnsSetupPage').then(m => ({ default: m.HelpDnsSetupPage })))
const SetupTrialPage = lazy(() => import('./pages/SetupTrialPage').then(m => ({ default: m.SetupTrialPage })))
const TemplatesPage = lazy(() => import('./pages/TemplatesPage').then(m => ({ default: m.TemplatesPage })))
const TemplateEditorPage = lazy(() => import('./pages/TemplateEditorPage').then(m => ({ default: m.TemplateEditorPage })))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
})

function PageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  )
}

// Catches OAuth errors that Supabase puts on the Site URL when an auth flow
// fails (e.g. ?error=invalid_request&error_code=bad_oauth_state when the user
// takes too long on Google's consent page and the state param expires).
// Without this the user lands on the home page with cryptic query params and
// no indication of what went wrong. We redirect them to /login with a clear
// error message in route state.
function OAuthErrorRedirect() {
  const location = useLocation()
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const errorCode = params.get('error_code')
    const errorDescription = params.get('error_description')
    if (!errorCode && !errorDescription) return
    // Cleanup URL to avoid showing the params on the next render
    window.history.replaceState({}, '', location.pathname)
    // Map known codes to friendly copy; fall back to error_description for the rest
    let message = errorDescription?.replace(/\+/g, ' ') ?? 'Sign-in failed. Please try again.'
    if (errorCode === 'bad_oauth_state' || /state has expired/i.test(message)) {
      message = 'Your Google sign-in session expired (you can take up to 10 minutes). Please try again.'
    } else if (errorCode === 'access_denied' || /access.denied/i.test(message)) {
      message = 'You declined Google access. Try again and accept the permissions to continue.'
    }
    // Hard navigate (rather than React Router) so route state isn't blown away
    // by the cleanup above. We pass the message via sessionStorage because
    // window.history.replaceState dropped it from URL.
    sessionStorage.setItem('oauth_error_message', message)
    // Detect signup-in-progress: SignupPage stashes these flags before kicking
    // off Google OAuth. If any are present, the user is mid-signup (not a
    // returning user trying to log in) — send them back to /signup so they
    // can retry without losing the V2 prefill chain.
    const signupInProgress =
      sessionStorage.getItem('signup_from_v2') === '1' ||
      sessionStorage.getItem('signup_plan_tier') !== null ||
      sessionStorage.getItem('signup_demo_token') !== null
    window.location.replace(signupInProgress ? '/signup' : '/login')
  }, [location])
  return null
}

// Redirects to /onboarding if the user hasn't completed it yet.
// Only triggers when onboarding_completed is explicitly false (new users).
// Existing users with null are unaffected.
function OnboardingGuard() {
  const { data: profile, isLoading } = useProfile()
  const location = useLocation()
  const { i18n } = useTranslation()

  // Sync profile language preference on FIRST load only (cross-device sync).
  // Uses a ref so it never re-runs after the user manually switches language
  // via LanguageSwitcher (which updates the DB async — the cached profile
  // would still show the old language and reset it back).
  const hasAppliedProfileLang = useRef(false)
  useEffect(() => {
    if (!hasAppliedProfileLang.current && profile?.preferred_language) {
      hasAppliedProfileLang.current = true
      if (profile.preferred_language !== i18n.language) {
        i18n.changeLanguage(profile.preferred_language)
        localStorage.setItem(USER_LANG_KEY, profile.preferred_language)
        document.documentElement.lang = profile.preferred_language
      }
    }
  }, [profile?.preferred_language, i18n])

  // While loading, render nothing (ProtectedRoute already shows a spinner)
  if (isLoading) return null

  // Trial setup must complete before anything else (handles Google OAuth signups)
  if (profile && profile.trial_setup_required) {
    return <Navigate to="/setup-trial" state={{ from: location.pathname }} replace />
  }

  if (profile && profile.onboarding_completed === false) {
    return <Navigate to="/onboarding" state={{ from: location.pathname }} replace />
  }

  return <Outlet />
}

export default function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        {!isProduction && (
          <Helmet>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
        )}
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <BrowserRouter>
              <OAuthErrorRedirect />
              <Suspense fallback={<PageSpinner />}>
                <Routes>
                  {/* Public routes */}
                  {/* Landing: V2 with hero email/website input that funnels into the
                      14-day trial signup. The classic landing is preserved at /classic
                      for reference / fallback. */}
                  <Route path="/" element={<LandingV2Page />} />
                  <Route path="/classic" element={<LandingPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/mockup" element={<MockupPage />} />
                  <Route path="/demo" element={<MetaAdsLandingPage />} />
                  <Route path="/demo/gracias" element={<MetaAdsThanksPage />} />
                  <Route path="/v2" element={<LandingV2Page />} />
                  {/* Paid-ads landing — unlinked from the home on purpose, noindex.
                      Reached only via ad URLs (Meta/Google). */}
                  <Route path="/analiza" element={<PaidLandingPage />} />
                  <Route path="/help" element={<HelpIndexPage />} />
                  <Route path="/help/:slug" element={<HelpDnsSetupPage />} />

                  {/* Protected routes */}
                  <Route element={<ProtectedRoute />}>
                    {/* Trial setup — captures card or validates demo token (Google OAuth users) */}
                    <Route path="/setup-trial" element={<SetupTrialPage />} />

                    {/* Onboarding — shown before the main app, no sidebar */}
                    <Route path="/onboarding" element={<OnboardingPage />} />

                    {/* Main app — requires onboarding completed */}
                    <Route element={<OnboardingGuard />}>
                      <Route element={<AppLayout />}>
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/campaigns" element={<CampaignsPage />} />
                        <Route path="/campaigns/new" element={<CampaignCreatePage />} />
                        <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
                        <Route path="/templates" element={<TemplatesPage />} />
                        <Route path="/templates/new" element={<TemplateEditorPage />} />
                        <Route path="/templates/:id" element={<TemplateEditorPage />} />
                        <Route path="/contacts" element={<ContactsPage />} />
                        <Route path="/analytics" element={<AnalyticsPage />} />
                        <Route path="/contacts/:id" element={<ContactDetailPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/account-health" element={<AccountHealthPage />} />
                        <Route path="/admin" element={<AdminDashboardPage />} />
                      </Route>
                    </Route>
                  </Route>

                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </AuthProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  )
}
